import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Modal from 'utils/Modal'
import callApi from 'services/api'
import { useNavigate } from 'react-router-dom'
import { AlertState } from 'global/AlertState'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { loadingState } from 'global/LoaderState'

const AddSubAdmin = ({
  showModal,
  handleCloseModal,
  fetchEmployees,
  editingData,
}) => {
  const [, setAlertMessage] = useRecoilState(AlertState)
  const loader = useSetRecoilState(loadingState)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [unitList, setUnitList] = useState([])
  const [subUnitList, setSubUnitList] = useState([])

  const navigate = useNavigate()

  const getList = async (params) => {
    try {
      const response = await callApi(
        `filtermaster?${params}`,
        'GET',
        null,
        true,
        true,
        setAlertMessage,
        navigate
      )

      if (response.success) {
        return response.body.data
      }
    } catch (err) {
      setError('Failed to fetch ')
    }
  }

  // ✅ Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    mobileNumber: Yup.string()
      .matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits')
      .required('Mobile number is required'),
    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required'),
    unit: Yup.string().required('Unit is required'),
    subUnit: Yup.string().when('unit', {
      is: () => Array.isArray(subUnitList) && subUnitList.length > 0,
      then: (schema) => schema.required('Sub Unit is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  })

  // ✅ Initial form values
  const [initialValues, setInitialValues] = useState({
    name: '',
    mobileNumber: '',
    email: '',
    unit: '',
    subUnit: '',
  })

  // ✅ Formik setup
  const formik = useFormik({
    initialValues,

    enableReinitialize: true,
    validationSchema,
    context: { subUnitList },
    onSubmit: async (values, { resetForm }) => {
      setLoading(true)
      try {
        setLoading(true)

        const response = await callApi(
          'auth/signup',
          'POST',
          values,
          true,
          true,
          setAlertMessage
        )

        if (response?.success) {
          setAlertMessage(
            `The system has successfully created a user profile with email as username and mobile number as  password for authentication.|true`
          )

          if (fetchEmployees) fetchEmployees()
          resetForm()
          handleCloseModal()
        } else {
          // API responded but not successful
          setAlertMessage(response?.body?.detail || 'Failed to create user.')
        }
      } catch (error) {
        // API call failed (network/server error)

        setAlertMessage(error?.detail || 'Failed to create user.')
      } finally {
        setLoading(false)
      }
    },
  })

  const getUnits = async (id) => {
    const params = new URLSearchParams({
      filter: id
        ? JSON.stringify({ parentId: id })
        : JSON.stringify({ type: 'unit' }),
      sort_order: '1',
      page: '1',
      limit: '1000',
    })
    const data = await getList(params)
    if (id) {
      setSubUnitList(data)
    } else {
      setUnitList(data)
    }
  }

  const handleUnitClick = (e) => {
    e.preventDefault()
    const data = e.target.value
    getUnits(data)
    formik.setFieldValue('unit', data)
  }
  useEffect(() => {
    if (showModal) {
      getUnits()
    }
  }, [showModal])
  // ✅ Reset form when modal closes
  useEffect(() => {
    if (!showModal) {
      formik.resetForm()
      setUnitList([])
      setSubUnitList([])
      setError(null)
    }
  }, [showModal])

  return (
    <Modal
      showModal={showModal}
      handleCloseModal={handleCloseModal}
      title={editingData ? 'Update User' : 'Add User'}
      modalWidth={{ width: '600px' }}
    >
      {error && <div className="alert alert-danger">{error}</div>}
      <form onSubmit={formik.handleSubmit}>
        {/* Name */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Name <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <input
              type="text"
              className="form-control"
              name="name"
              placeholder="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.name && formik.errors.name && (
              <div className="text-danger">{formik.errors.name}</div>
            )}
          </div>
        </div>

        {/* Mobile Number */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Mobile Number <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <input
              type="text"
              className="form-control"
              name="mobileNumber"
              placeholder="10-digit Mobile Number"
              value={formik.values.mobileNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              maxLength="10"
            />
            {formik.touched.mobileNumber && formik.errors.mobileNumber && (
              <div className="text-danger">{formik.errors.mobileNumber}</div>
            )}
          </div>
        </div>

        {/* Email */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Email <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <input
              type="email"
              className="form-control"
              name="email"
              placeholder="Email Address"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="text-danger">{formik.errors.email}</div>
            )}
          </div>
        </div>

        {/* Unit */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Unit <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <select
              className="form-control"
              name="unit"
              value={formik.values.unit}
              onChange={handleUnitClick}
              onBlur={formik.handleBlur}
            >
              <option value="">Select Unit</option>
              {unitList?.map((opt) => (
                <option key={opt.id || opt.value} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
            {formik.touched.unit && formik.errors.unit && (
              <div className="text-danger">{formik.errors.unit}</div>
            )}
          </div>
        </div>
        {/* subUnit */}
        {subUnitList.length > 0 && (
          <div className="form-group row mb-3">
            <label className="col-sm-4 col-form-label">
              Sub Unit <span style={{ color: 'red' }}>*</span>
            </label>
            <div className="col-sm-8">
              <select
                className="form-control"
                name="subUnit"
                value={formik.values.subUnit}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select Sub Unit</option>
                {subUnitList?.map((opt) => (
                  <option key={opt.id || opt.value} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              {formik.touched.subUnit && formik.errors.subUnit && (
                <div className="text-danger">{formik.errors.subUnit}</div>
              )}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="d-flex justify-content-center mt-4">
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={handleCloseModal}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {editingData ? 'Update' : 'Submit'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default AddSubAdmin
